/**
 * What "offline practice" means, which until now it did not mean anything.
 *
 * `Offline lessons` is a bullet on the paywall and a row in the plan comparison on all
 * three clients, and nothing downloaded anything: `GET /content/manifest` had been in
 * the contract, implemented and indexed, with no client calling it. Selling a feature
 * that does not exist is the single most damaging review class in this category
 * (`docs/research/11-leader-reviews-2026-10-18.md` §2) and the reason
 * `tooling/scripts/verify-promises.mjs` exists.
 *
 * The policy lives here so the three platforms agree on what is downloaded, what a
 * download costs, and when it has gone stale. The storage is each platform's own.
 */

/** One downloadable thing, as the manifest describes it. */
export interface ManifestEntry {
  readonly kind: string;
  readonly id: string;
  readonly bytes: number;
}

export interface ContentManifest {
  readonly version: number;
  readonly added: readonly ManifestEntry[];
  readonly removed: readonly { readonly kind: string; readonly id: string }[];
  readonly totalBytes: number;
}

/** What the device already holds, as the platform's store reports it. */
export interface StoredContent {
  readonly version: number;
  readonly ids: readonly string[];
  readonly bytes: number;
}

export interface OfflinePlan {
  /** Entries to fetch and keep, in the order to fetch them. */
  readonly fetch: readonly ManifestEntry[];
  /** Ids to delete: unpublished upstream, or beyond the budget. */
  readonly evict: readonly string[];
  /** What the device will hold afterwards, for the storage total the spec asks for. */
  readonly resultingBytes: number;
}

/**
 * How much we are willing to occupy by default.
 *
 * A language course is not a reason to fill someone's phone. 250 MB holds a long way
 * past where anyone practises offline, and the screen shows the number either way.
 */
export const DEFAULT_OFFLINE_BUDGET_BYTES = 250 * 1024 * 1024;

/**
 * Decides what to download and what to drop.
 *
 * Smallest first: when the budget binds, twenty short lessons offline beats one long
 * one, and the learner on a plane cares about having *something* for every slot rather
 * than everything for one.
 */
export function planOfflineSync(
  manifest: ContentManifest,
  stored: StoredContent,
  budgetBytes: number = DEFAULT_OFFLINE_BUDGET_BYTES,
): OfflinePlan {
  const held = new Set(stored.ids);
  const removed = new Set(manifest.removed.map((entry) => entry.id));

  // Content pulled upstream goes whether or not we are downloading anything new: an
  // unpublished lesson is usually unpublished because it was wrong.
  const evict = [...held].filter((id) => removed.has(id));

  const candidates = [...manifest.added]
    .filter((entry) => !removed.has(entry.id))
    .sort((left, right) => left.bytes - right.bytes);

  const fetch: ManifestEntry[] = [];
  // What is already held counts against the budget first. Evicted entries are not
  // deducted: the manifest says which ids went, never how large they were, and
  // guessing a size would make the storage total we show wrong in the other direction.
  let bytes = stored.bytes;
  for (const entry of candidates) {
    if (held.has(entry.id)) continue;
    if (bytes + entry.bytes > budgetBytes) continue;
    fetch.push(entry);
    bytes += entry.bytes;
  }

  return { fetch, evict, resultingBytes: bytes };
}

/**
 * Whether what is stored still matches what the server publishes.
 *
 * A stale download is worse than none: practising a lesson that was corrected upstream
 * teaches the mistake. Version equality is the whole check, because the manifest is a
 * delta against a version.
 */
export function isOfflineContentStale(manifest: ContentManifest, stored: StoredContent): boolean {
  return stored.version !== manifest.version;
}

/**
 * The size, in the units people think in.
 *
 * Deliberately one decimal and MB-first: "0.4 MB" reads as small, "417 KB" reads as a
 * number to think about, and the storage row is there to reassure rather than to
 * invite arithmetic.
 */
export function formatBytes(bytes: number): string {
  if (bytes <= 0) return 'Nothing yet';
  const mb = bytes / (1024 * 1024);
  if (mb < 0.1) return 'Under 0.1 MB';
  if (mb < 1000) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}
